import { useEffect } from 'react'

// Calculate column menu position
export function useColumnMenuPositionEffect(
  showColumnMenu: boolean,
  columnMenuRef: React.RefObject<HTMLDivElement>,
  setColumnMenuPosition: React.Dispatch<React.SetStateAction<{ top: number; right: number } | null>>
) {
  useEffect(() => {
    if (!showColumnMenu || !columnMenuRef.current) {
      setColumnMenuPosition(null)
      return
    }

    const updatePosition = () => {
      const parent = columnMenuRef.current
      if (!parent) return

      const parentRect = parent.getBoundingClientRect()
      const viewportWidth = window.innerWidth

      // Calculate right position: distance from right edge of viewport to right edge of button
      const right = viewportWidth - parentRect.right
      // Position dropdown below the table header with more spacing to avoid overlap
      const top = parentRect.bottom + 8

      setColumnMenuPosition({ top, right })
    }

    updatePosition()
    const timeoutId = setTimeout(updatePosition, 100)

    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [showColumnMenu, columnMenuRef, setColumnMenuPosition])
}

// Close column menu when clicking outside
export function useColumnMenuClickOutsideEffect(
  showColumnMenu: boolean,
  columnMenuRef: React.RefObject<HTMLDivElement>,
  columnDropdownRef: React.RefObject<HTMLDivElement>,
  setShowColumnMenu: React.Dispatch<React.SetStateAction<boolean>>
) {
  useEffect(() => {
    if (!showColumnMenu) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const parent = columnMenuRef.current
      const dropdown = columnDropdownRef.current

      if (parent && !parent.contains(target) && dropdown && !dropdown.contains(target)) {
        setShowColumnMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showColumnMenu, columnMenuRef, columnDropdownRef, setShowColumnMenu])
}
